import {
	Autocomplete,
	Box,
	Button,
	Fade,
	Paper,
	TextField,
} from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useMemo } from 'react'
import {
	BaseEventListenerModel,
	EventHandler,
	EventListener,
} from '../../../../api/clients/events/DTO'
import {
	FormBuilderViewer,
	StandaloneCellManager,
} from '../../../../modules/FormBuilderInterop/Runtimes/Standalone'
import { toastService } from '../../../../services/notifications/ToastService'
import { FixedHeader } from '../../../../utils/HOC/FixedHeaders'
import { compareState } from '../../../../utils/compareState'

type ConfigureEventListenerSectionProps = {
	eventListenerId: number
	eventListener: EventListener
	eventHandler: EventHandler
	eventIds: string[]
	existingEventListenerNames: string[]

	onUpdateEventListener: (newModel: BaseEventListenerModel) => Promise<void>
}

export const ConfigureEventListenerSection = observer(
	(props: ConfigureEventListenerSectionProps) => {
		const localStore = useLocalObservable(() => ({
			eventHandlerState:
				props.eventListener.eventHandlerState ??
				({} as Record<string, unknown>),
			eventListenerName: props.eventListener.displayName,
			eventListenerDescription: props.eventListener.description,
			nameIsDuplicate: false,
			eventId: props.eventListener.eventId as string | undefined,
		}))

		const cellManager = useMemo(() => {
			return new StandaloneCellManager(
				props.eventHandler.formBuilderSchema,
				localStore.eventHandlerState,
			)
		}, [props.eventListener, props.eventHandler, localStore.eventHandlerState])

		const handleUpdateEventListener = async () => {
			if (localStore.eventId === undefined) return

			const baseModel: BaseEventListenerModel = {
				displayName: localStore.eventListenerName,
				description: localStore.eventListenerDescription,
				eventHandlerState: localStore.eventHandlerState,
				eventHandlerId: props.eventListener.eventHandlerId,
				eventId: localStore.eventId,
			}

			try {
				props.onUpdateEventListener(baseModel)

				toastService.displayToast({
					message: 'Event listener updated',
					area: 'global',
					severity: 'success',
				})
			} catch (e) {
				console.error(e)

				toastService.displayToast({
					message: 'Error updating event listener',
					area: 'global',
					severity: 'error',
				})
			}
		}

		const isDisabled = (): boolean => {
			// if no values have changed we want the button to be disabled
			if (localStore.eventId == undefined) return true

			if (
				!compareState(
					props.eventListener.eventHandlerState as Record<string, unknown>,
					localStore.eventHandlerState,
				)
			)
				return false

			return (
				localStore.eventListenerName === props.eventListener.displayName &&
				localStore.eventListenerDescription ===
				props.eventListener.description &&
				localStore.eventId === props.eventListener.eventId
			)
		}

		return (
			<Fade in>
				<Box display="flex" flexDirection="column" height="100%" width="100%">
					<Box
						component={Paper}
						marginBottom={2}
						paddingY={1}
						paddingX={2}
						width="100%"
					>
						<Box display="flex" flexDirection="column" gap={1}>
							<Box display="flex" gap={2} width="100%">
								<Box width="100%">
									<FixedHeader label="Name">
										<TextField
											value={localStore.eventListenerName}
											onChange={action(
												(evt) =>
													(localStore.eventListenerName = evt.target.value),
											)}
											fullWidth
											size="small"
										/>
									</FixedHeader>
								</Box>
								<Box width="100%">
									<FixedHeader label="Event">
										<Autocomplete
											fullWidth
											size="small"
											defaultValue={localStore.eventId}
											options={props.eventIds}
											renderInput={(params) => (
												<TextField {...params} fullWidth />
											)}
											onChange={(_, value) => {
												runInAction(() => {
													localStore.eventId = value ?? undefined
												})
											}}
										/>
									</FixedHeader>
								</Box>
							</Box>
							{cellManager !== undefined && (
								<FormBuilderViewer cellManager={cellManager} />
							)}
						</Box>
					</Box>
					<Box alignSelf={'flex-end'} paddingBottom={3} gap={2} display="flex">
						<Button
							color="primary"
							variant="contained"
							onClick={handleUpdateEventListener}
							disabled={isDisabled()}
						>
							Save Changes
						</Button>
					</Box>
				</Box>
			</Fade>
		)
	},
)
